import { useQuery } from '@tanstack/react-query';

import {
  universeQueryFields,
  type FilterKey,
  type UniverseFilter,
  type WindowKey,
} from '@meridian/contracts';

import { dashboardKeys } from '../query-keys/dashboard';
import { useRepository } from '../repository-context';

/**
 * Dashboard composite query. Pass `asOf` from a settled `useMetaQuery()` so
 * cache keys rotate when the published snapshot advances.
 * Tri-state `asOf`: `undefined` disables the query (meta not success); `null`
 * is the synthetic/unpublished key; a date string is the live snapshot key.
 * The query key always normalizes via `asOf ?? null` (disabled entries still
 * occupy the null slot). Argument order matches `dashboardKeys.detail`.
 */
export function useDashboardQuery(
  asOf: string | null | undefined,
  window: WindowKey,
  index: FilterKey,
  filters?: UniverseFilter,
) {
  const repo = useRepository();
  return useQuery({
    queryKey: dashboardKeys.detail(asOf ?? null, window, index, filters),
    queryFn: () =>
      repo.getDashboard({
        window,
        index,
        ...universeQueryFields(filters),
      }),
    enabled: asOf !== undefined,
  });
}

/**
 * The manual book (ADR-0042 · #84). NOT keyed by asOf: a nightly publish cannot
 * change what the operator owns, so re-fetching it on every snapshot advance
 * would be churn. Always enabled — a screen marks holdings whether or not the
 * book FILTER is engaged, and an empty book is a valid, renderable answer.
 * The shell passes `enabled` so `/login` never probes `/v1/book`.
 */
export function useBookQuery(enabled = true) {
  const repo = useRepository();
  return useQuery({
    queryKey: dashboardKeys.book(),
    queryFn: () => repo.getBook(),
    enabled,
  });
}

/**
 * Vendor last/prev-close quotes. Keyed by asOf only — independent of win/idx.
 * The `'snapshot'` segment keeps this out of `dashboardKeys.detail` slots.
 */
export function useMarketSnapshotQuery(asOf: string | null | undefined) {
  const repo = useRepository();
  return useQuery({
    queryKey: dashboardKeys.snapshot(asOf ?? null),
    queryFn: () => repo.getMarketSnapshot(),
    enabled: asOf !== undefined,
  });
}

/**
 * SPY adjusted-close window (ADR-0043 · #82). Additive: a failure here must
 * not tear down the screen that asked — the caller treats missing data as
 * a null excess, never as a page-level error.
 * Same tri-state `asOf` as `useDashboardQuery` so a stock hop reuses the
 * dashboard's cached series when the window matches.
 */
export function useBenchmarkSeriesQuery(asOf: string | null | undefined, window: WindowKey) {
  const repo = useRepository();
  return useQuery({
    queryKey: dashboardKeys.benchmarkSeries(asOf ?? null, window),
    queryFn: () => repo.getBenchmarkSeries({ window }),
    enabled: asOf !== undefined,
  });
}
